import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { Paper, Button, Checkbox, FormControlLabel } from '@material-ui/core';
import { useAlert } from '../common/AlertProvider/AlertProvider';
import { formatPhone } from '../../utils/format-utils';
import { sendAuthCode } from '../../apiCalls/user/user';
import { withLocal } from '../common/LocaleProvider/LocaleProvider';
import routes from '../../constants/routes';
import usaAreaCodes from '../../constants/usaAreaCodes';
import { Consent, Footer } from '../common';
import {
  setToken,
  setMobileNumber,
  incrementVoiceCallFailure,
  setCallChoice,
  getSmsOptInStatus,
  setSmsOptInStatus

} from '../../utils/session-utils';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  },
  checkbox: {
    color: theme.palette.primary.main
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px'
  },
  header: {
    margin: '16px 0px',
    fontFamily: 'times',
    color: theme.palette.primary.dark
  },
  form: {
    display: 'flex',
    flexDirection: 'column'
  },
  instructions: {
    fontSize: '10px',
    marginTop: '16px'
  },
  error: {
    color: '#e02020'
  }
}));

const isUsaAreaCode = phoneNumber => {
  // (999)999-9999  is 13 characters
  if (!phoneNumber || phoneNumber.length !== 13) {
    return false;
  }
  const areaCode = phoneNumber.slice(1, 4);
  return usaAreaCodes.has(areaCode);
};

const HomePage = ({ history, locale }) => {
  const classes = useStyles();

  const { setAlert } = useAlert();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [callOption, setCallOption] = useState(false);
  const [optInChecked, setOptInChecked] = useState(getSmsOptInStatus());
  const [error, setError] = useState(null);

  const onSubmit = () => {
    setError(null);
    setDisableSubmit(true);

    setSmsOptInStatus(optInChecked);
    incrementVoiceCallFailure();


    const mobileNumber = phoneNumber.replace(/[-() ]/g, '');

    sendAuthCode(
      {
        mobileNumber,
        message: callOption ? '%s' : locale.verify_code_message,
        voiceOption: callOption
      },
      res => {
        setMobileNumber(mobileNumber);
        setToken(res.token);
        setCallChoice(callOption);
        setAlert({
          text: 'Login successful, Code not required',
          type: 'success'
        });

        history.push(
          res.neverShowAgain
            ? routes.CREATE_ACCOUNT
            : routes.PRE_SCREEN_INSRUCTIONS
        );
      },
      err => {
        if (err.response && err.response.status === 401) {
          setMobileNumber(mobileNumber);
          setCallChoice(callOption);
          setAlert({ text: 'Verification required', type: 'info' });
          history.push(routes.ENTER_CODE);
        } else {
          setDisableSubmit(false);
          err.response
            ? setError(err.response.data.message)
            : setError(err.message);
        }
      }
    );
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>

        {<h2>{locale.home.try_voice_call}</h2>}

        <h1 className={classes.header}>{locale.header}</h1>
        <p style={{ margin: '0px' }}>{locale.home.self_assessment}</p>
        <p style={{ color: '#1B4297', marginBottom: 0 }}>
          {locale.home.thank_you}
        </p>
        <p style={{ marginBottom: 0 }}>{locale.home.enter_cell}</p>
        <p>
          <b>{locale.home.own_phone}</b>
        </p>
        <ValidatorForm className={classes.form} onSubmit={() => onSubmit()}>
          {error && <span className={classes.error}>{error}</span>}
          <TextValidator
            id="phone-number"
            name="phoneNumber"
            inputMode="numeric"
            className={classes.textfield}
            label={`${locale.input_labels.mobile_phone} *`}
            placeholder="999-999-9999"
            value={phoneNumber}
            onChange={e => setPhoneNumber(formatPhone(e.target.value))}
            validators={['required', 'minStringLength:13']}
            errorMessages={[
              'this field is required',
              'phone number is invalid'
            ]}
            variant="outlined"
            inputProps={{
              inputMode: 'numeric',
              'data-testid': 'phone-number'
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={optInChecked}
                onChange={event => {
                  setOptInChecked(event.target.checked);
                }}
                data-testid="opt-in-checkbox"
                className={classes.checkbox}
                color="primary"
              />
            }
            label={`${locale.input_labels.opt_in_messages} *`}
          />
          <FormControlLabel
            control={
              <Checkbox
                disabled={!phoneNumber || !isUsaAreaCode(phoneNumber)}
                onChange={event => {
                  setCallOption(event.target.checked);
                }}
                data-testid="callme-checkbox"
                className={classes.checkbox}
                color="primary"
              />
            }
            label="Call me instead (USA only)"
          />
          <Button
            id="btn-send-validate"
            className={classes.button}
            disabled={!phoneNumber || !optInChecked || disableSubmit}
            type="submit"
            color="primary"
            variant="contained"
          >
            {locale.button_labels.send_verification}
          </Button>
        </ValidatorForm>
        <div className={classes.instructions}>
          <p>{locale.home.enter_mobile}</p>
          <p>
            <b>{locale.home.alternative_options}</b>
          </p>
         {/* <p>{locale.home.ascension_connect}</p> */}
         <p><b>{locale.home.business_hours_header}</b></p>
        <ul>
          <li>{locale.home.business_hours_contact}
            <a
              href="https://docs.google.com/spreadsheets/d/1CyI79U-oK0xtptZNGCZbtdjMhbvsVm3XrlIjpnKvmeo/edit?pli=1&gid=157795906#gid=157795906"
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: 'pointer', color: '#1B4297' }}
            >
              {locale.home.business_hours_link_text}
            </a>
            .
          </li>
        </ul>
        
        <p><b>{locale.home.after_hours_header}</b></p>
        <ul>
          <li>
            {locale.home.after_hours_no_work}
            <ul>
              {locale.home.after_hours_symptoms.map((symptom, index) => (
                <li key={`symptom-${index}`}>{symptom}</li>
              ))}
            </ul>
          </li>
          <li dangerouslySetInnerHTML={{ __html: locale.home.after_hours_normal_process }} />
          <li dangerouslySetInnerHTML={{ __html:locale.home.after_hours_contact_supervisor}} />
          <li>{locale.home.after_hours_contact_aoh}
            <a
              href="https://docs.google.com/spreadsheets/d/1CyI79U-oK0xtptZNGCZbtdjMhbvsVm3XrlIjpnKvmeo/edit?pli=1&gid=157795906#gid=157795906"
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: 'pointer', color: '#1B4297' }}
            >
              {locale.home.business_hours_link_text}
            </a>
            .
          </li>
        </ul>
        </div>
        <Consent locale={locale} />
        <Footer locale={locale} />
      </Paper>
    </div>
  );
};

export default withLocal(HomePage);
